import {Component, OnDestroy, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {RewardCatalogType, RewardsAdminService} from '../../../../services/rewards-admin/rewards-admin.service';
import {Router} from '@angular/router';
import {MatDialog} from '@angular/material/dialog';
import {SuperadminRewardDetailComponent} from '../details/superadmin-reward-detail.component';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import {
  parseRewardCampaignRules,
  rewardCampaignAppliesToAllBrands,
} from '../../../../helpers/reward-campaign-rules.helper';

@Component({
    selector: 'app-superadmin-rewards-list',
    templateUrl: './superadmin-rewards-list.component.html',
    styleUrls: ['./superadmin-rewards-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SuperadminRewardsListComponent implements OnInit, OnDestroy {
  public items: any[] = [];
  public campaigns: Record<string, any[]> = {};
  public nameSearch = '';
  public titleSearch = '';
  public campaignSearch = '';
  public fullTextSearch = '';
  public catalogType: RewardCatalogType = 'all';
  public readonly catalogTypeOptions: Array<{ value: RewardCatalogType; label: string }> = [
    { value: 'all', label: 'All' },
    { value: 'reward', label: 'Rewards' },
    { value: 'snack', label: 'Snacks' },
  ];
  public page = 1;
  public limit = 30;
  public endOfList = false;
  public loading = false;
  public readonly allBrandsCampaignTooltip = 'No brands are defined in Rules JSON. This campaign applies to all brands.';
  private readonly searchDebounceMs = 300;
  private readonly searchValueChanges = new Subject<string>();
  private readonly searchSubscription: Subscription;

  constructor(
    private rewardsAdminService: RewardsAdminService,
    private router: Router,
    private dialog: MatDialog,
  ) {
    this.searchSubscription = this.searchValueChanges
      .pipe(
        debounceTime(this.searchDebounceMs),
        distinctUntilChanged(),
      )
      .subscribe(() => this.loadItems(true));
  }

  ngOnInit(): void {
    this.loadItems(true);
    this.rewardsAdminService.monitorRewardsChanged(() => this.loadItems(true));
  }

  ngOnDestroy(): void {
    this.searchSubscription.unsubscribe();
  }

  async loadItems(clear = false): Promise<void> {
    if (clear) {
      this.page = 1;
      this.endOfList = false;
      this.items = [];
    }
    if (this.loading || this.endOfList) {
      return;
    }
    this.loading = true;
    try {
      const rewards = await this.rewardsAdminService.getRewards(
        this.buildFilter(),
        this.page,
        this.limit,
        this.catalogType,
      );
      if (!rewards || rewards.length === 0) {
        this.endOfList = true;
      } else {
        this.items = this.items.concat(rewards);
        this.page += 1;
        rewards.forEach((reward: any) => this.loadCampaigns(reward));
      }
    } catch (error) {
      console.error('Error loading rewards', error);
    } finally {
      this.loading = false;
    }
  }

  async loadCampaigns(reward: any): Promise<void> {
    try {
      const campaigns = await this.rewardsAdminService.getCampaignsForReward(reward.id);
      const now = new Date().toISOString();
      this.campaigns[reward.id] = (campaigns || []).map((campaign: any) => {
        const state = campaign.endAt < now ? 'Closed' : 'Open';
        const rules = parseRewardCampaignRules(campaign.rules);
        const allBrands = rewardCampaignAppliesToAllBrands(rules);
        return {
          name: campaign.campaignName + (allBrands ? ' [All]' : ` [${this.getCampaignBrandLabel(rules.brands)}]`),
          state,
          allBrands,
        };
      });
    } catch (error) {
      console.error('Error loading campaigns for reward', error);
    }
  }

  queueFilter(): void {
    this.searchValueChanges.next(this.buildFilter());
  }

  onCatalogTypeChange(): void {
    this.loadItems(true);
  }

  addReward(): void {
    const dialogRef = this.dialog.open(SuperadminRewardDetailComponent, {
      width: '960px',
      maxWidth: '95vw',
      data: { rewardId: 'new' },
    });
    dialogRef.afterClosed().subscribe((changed) => {
      if (changed) {
        this.loadItems(true);
      }
    });
  }

  editReward(reward: any): void {
    const dialogRef = this.dialog.open(SuperadminRewardDetailComponent, {
      width: '960px',
      maxWidth: '95vw',
      data: { rewardId: reward.id, reward },
    });
    dialogRef.afterClosed().subscribe((changed) => {
      if (changed) {
        this.loadItems(true);
      }
    });
  }

  showClaimed(event: Event, reward: any): void {
    event.stopPropagation();
    this.router.navigate(['/superadmin/rewards/claimed-reward', reward.id]).then();
  }

  clone(event: Event, reward: any): void {
    event.stopPropagation();
    const newReward = { ...reward };
    newReward.id = undefined;
    newReward.name = `${reward.name} - COPY`;
    if (newReward.rewardDetails?.clo?.id) {
      newReward.rewardDetails.clo.id = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    }
    this.rewardsAdminService.postReward(newReward).then();
  }

  importRewards(): void {
    this.router.navigate(['/superadmin/rewards/import']).then();
  }

  exportRewards(): void {
    this.router.navigate(['/superadmin/rewards/export']).then();
  }

  onScroll(event: any): void {
    const scrollTop = event.target.scrollTop;
    const scrollHeight = event.target.scrollHeight;
    const offsetHeight = event.target.offsetHeight;
    const threshold = 150;
    if (scrollTop + offsetHeight >= scrollHeight - threshold) {
      this.loadItems();
    }
  }

  private buildFilter(): string {
    const segments = [
      this.buildScopedFilter('name', this.nameSearch),
      this.buildScopedFilter('title', this.titleSearch),
      this.buildScopedFilter('campaign', this.campaignSearch),
      this.formatPlainTextFilter(this.fullTextSearch),
    ].filter(Boolean);

    return segments.join(' ');
  }

  private getCampaignBrandLabel(brands: any): string {
    if (Array.isArray(brands)) {
      return brands.join(':');
    }

    return String(brands || '');
  }

  private buildScopedFilter(field: string, value: string): string {
    const normalizedValue = this.normalizeFilterValue(value);
    if (!normalizedValue) {
      return '';
    }

    return `${field}:${normalizedValue}`;
  }

  private formatPlainTextFilter(value: string): string {
    return this.normalizeFilterValue(value);
  }

  private normalizeFilterValue(value: string): string {
    const normalizedValue = (value || '').trim();
    if (!normalizedValue) {
      return '';
    }

    if (/[\s"]/u.test(normalizedValue)) {
      return `"${normalizedValue.replace(/"/g, '\\"')}"`;
    }

    return normalizedValue;
  }
}
